import React from 'react';
import type { FormikProps } from 'formik';
import { Input, Modal, Select, Spin } from 'antd';
import get from 'lodash/get';
import { ImageInput } from '~components/image-input';
import { ImageHolder } from '~components/image-holder';
import { ErrorAwareLabel } from '~components/error-aware-label';
import { LabeledField } from '~components/form-fields/labeled-field';
import { Section } from '~components/form-fields/section';
import { TextField } from '~components/form-fields/text-field';
import { TimezoneSelect } from '~components/timezone-select';
import { BackgroundColorPicker } from '~components/background-color-picker';
import { BACKGROUND_COLORS } from '~components/background-color-picker/constants';
import {
    Acvt,
    AcvtCanvas,
    AcvtHomePage,
} from '~components/advanced-ct-visualisation-tool';
import {
    PreviewBottomTabBar,
    PreviewProvider,
    PreviewThemeProvider,
    PreviewTabKey,
} from '~components/preview';
import MainBanner from '~components/main-banner';
import type { GameForSelect } from '~stores/games';
import type { VisiblePromotionRow } from '~stores/promotion';
import type { FeaturedForm as FeaturedFormValues } from '~stores/featured';
import * as Colors from '~styles/colors';
import { AcvtSupportedLanguages } from '~types/acvt';
import { ApplicationTargetEnum } from '~types/app-selection';
import { localizeLanguage } from '~utils/languages';
import { ConfigSection } from '../ConfigSection';
import { ScheduleSection } from '../ScheduleSection';
import style from '../../style.module.scss';

const SUGGESTED_TITLE_LENGTH = 18;
const SUGGESTED_SUBTITLE_LENGTH = 28;
const MAX_LENGTH = 255;

interface FeaturedFormProps {
    action: 'create' | 'edit';
    formik: FormikProps<FeaturedFormValues>;
    isFetching: boolean;
    promotionsForSelect: VisiblePromotionRow[];
    promotionsLoading: boolean;
    gamesForSelect: GameForSelect[];
    gamesLoading: boolean;
}

export const FeaturedForm = ({
    action,
    formik,
    isFetching,
    promotionsForSelect,
    promotionsLoading,
    gamesForSelect,
    gamesLoading,
}: FeaturedFormProps) => {
    const [lang, setLang] = React.useState<AcvtSupportedLanguages>('en');
    const [acvtItemsIndex, setAcvtItemsIndex] = React.useState(0);
    const [selectedTab, setSelectedTab] = React.useState<PreviewTabKey>('home');
    const [modalUrl, setModalUrl] = React.useState<string | undefined>();
    const [isModalVisible, setModalVisible] = React.useState<boolean>(false);
    const setFeaturedFieldValue = formik.setFieldValue;
    const featuredValues = formik.values;
    const featuredItems = featuredValues.items;

    React.useEffect(() => {
        featuredItems.forEach((item, i) => {
            if (item.backgroundColor === null && item.titleColor !== null) {
                setFeaturedFieldValue(`items[${i}].titleColor`, null);
            }
            if (item.backgroundColor === null && item.subtitleColor !== null) {
                setFeaturedFieldValue(`items[${i}].subtitleColor`, null);
            }
            if (item.backgroundColor === null && item.buttonColor !== null) {
                setFeaturedFieldValue(`items[${i}].buttonColor`, null);
            }
        });
    }, [featuredItems, setFeaturedFieldValue]);

    React.useEffect(() => {
        setAcvtItemsIndex(lang === 'fr' ? 1 : 0);
    }, [lang, featuredItems]);

    const checkIfHasError = (path: string) => {
        const hasError = Boolean(get(formik.errors, path));
        const isTouched = Boolean(get(formik.touched, path));
        return hasError && (isTouched || action !== 'create');
    };

    const changeLanguage = (language: AcvtSupportedLanguages) => {
        setLang(language);
    };

    // A promotion can be saved before its bilingual content is filled in, so
    // the option row may have no items. Fall back to the id for the label.
    const promotionOptionLabels = (promo: VisiblePromotionRow) => {
        const primary = promo.items[0];
        const secondary = promo.items[1];
        const fallback = `Promotion #${promo.id}`;

        return {
            title: primary?.title || fallback,
            subTitle: secondary?.title || '',
            thumbnail: primary?.thumbnail || undefined,
        };
    };

    return (
        <>
            <Modal
                open={isModalVisible}
                title="Image"
                footer={null}
                onCancel={() => setModalVisible(false)}
            >
                <img
                    alt="thumbnail"
                    className={style.full_width}
                    src={modalUrl ?? ''}
                />
            </Modal>

            <div className="content_with_margins">
                <form
                    className={
                        isFetching
                            ? style.featured_form_hidden
                            : style.featured_form
                    }
                >
                    <div className={style.featured_page_content}>
                        <div className={style.left_side}>
                            {isFetching && <Spin />}

                            <ConfigSection
                                values={formik.values}
                                setFieldValue={formik.setFieldValue}
                            />

                            <Section title="Timezone">
                                <LabeledField helperText="The timezone for all dates and times in this featured item.">
                                    <TimezoneSelect
                                        value={formik.values.timezone}
                                        onChange={(value) =>
                                            formik.setFieldValue(
                                                'timezone',
                                                value
                                            )
                                        }
                                        style={{ width: 320 }}
                                    />
                                </LabeledField>
                            </Section>

                            <ScheduleSection
                                values={formik.values}
                                setFieldValue={formik.setFieldValue}
                                checkIfHasError={checkIfHasError}
                            />

                            {formik.values.type === 'promotion' && (
                                <Section title="Promotion">
                                    <div className={style.item_padding}>
                                        <LabeledField label="Promotion">
                                            <Select
                                                listHeight={512}
                                                showSearch
                                                optionLabelProp="label"
                                                filterOption={false}
                                                loading={promotionsLoading}
                                                value={
                                                    formik.values.promotionId ??
                                                    undefined
                                                }
                                                onChange={(value) =>
                                                    formik.setFieldValue(
                                                        'promotionId',
                                                        value
                                                    )
                                                }
                                                className={
                                                    style.select_standard_width
                                                }
                                            >
                                                {promotionsForSelect.map(
                                                    (promo) => {
                                                        const labels = promotionOptionLabels(
                                                            promo
                                                        );

                                                        return (
                                                            <Select.Option
                                                                key={promo.id}
                                                                value={promo.id}
                                                                label={
                                                                    labels.title
                                                                }
                                                            >
                                                                <div
                                                                    className={
                                                                        style.custom_option
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            style.titles_holder
                                                                        }
                                                                    >
                                                                        <span>
                                                                            {
                                                                                labels.title
                                                                            }
                                                                        </span>
                                                                        <span>
                                                                            {
                                                                                labels.subTitle
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <ImageHolder
                                                                        src={
                                                                            labels.thumbnail
                                                                        }
                                                                        size={
                                                                            40
                                                                        }
                                                                        onClick={
                                                                            labels.thumbnail
                                                                                ? (
                                                                                      e
                                                                                  ) => {
                                                                                      e.stopPropagation();
                                                                                      setModalVisible(
                                                                                          true
                                                                                      );
                                                                                      setModalUrl(
                                                                                          labels.thumbnail
                                                                                      );
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                    />
                                                                </div>
                                                            </Select.Option>
                                                        );
                                                    }
                                                )}
                                            </Select>
                                        </LabeledField>
                                    </div>
                                </Section>
                            )}

                            {formik.values.type === 'games' && (
                                <Section title="Games">
                                    <div className={style.item_padding}>
                                        <LabeledField label="Games">
                                            <Select
                                                listHeight={512}
                                                showSearch
                                                optionLabelProp="label"
                                                filterOption={false}
                                                loading={gamesLoading}
                                                value={
                                                    formik.values.gameTypeId ||
                                                    ''
                                                }
                                                onChange={(value) => {
                                                    formik.setFieldValue(
                                                        'gameType',
                                                        value.substring(
                                                            0,
                                                            value.indexOf('_')
                                                        )
                                                    );
                                                    formik.setFieldValue(
                                                        'gameId',
                                                        Number(
                                                            value.substring(
                                                                value.indexOf(
                                                                    '_'
                                                                ) + 1
                                                            )
                                                        )
                                                    );
                                                    formik.setFieldValue(
                                                        'gameTypeId',
                                                        value
                                                    );
                                                }}
                                                className={
                                                    style.select_standard_width
                                                }
                                            >
                                                {gamesForSelect.map((game) => (
                                                    <Select.Option
                                                        key={`${game.type}_${game.id}`}
                                                        value={`${game.type}_${game.id}`}
                                                        label={game.title.en}
                                                    >
                                                        <div
                                                            className={
                                                                style.custom_option
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    style.titles_holder
                                                                }
                                                            >
                                                                <span>
                                                                    {
                                                                        game
                                                                            .title
                                                                            .en
                                                                    }
                                                                </span>
                                                                <span>
                                                                    {
                                                                        game
                                                                            .title
                                                                            .fr
                                                                    }
                                                                </span>
                                                            </div>
                                                            <ImageHolder
                                                                src={
                                                                    game.image
                                                                        .en
                                                                }
                                                                size={40}
                                                            />
                                                        </div>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </LabeledField>
                                    </div>
                                </Section>
                            )}

                            <Section title="Content">
                                <div className="columns_layout">
                                    {formik.values.items.map((item, i) => {
                                        const localizedLanguage = localizeLanguage(
                                            item.language
                                        );
                                        return (
                                            <div key={i} className="column">
                                                <span
                                                    className={
                                                        style.language_header
                                                    }
                                                >
                                                    {localizedLanguage}
                                                </span>

                                                <LabeledField
                                                    label={`Background image (${localizedLanguage})`}
                                                >
                                                    <ImageInput
                                                        url={
                                                            item.backgroundImage
                                                        }
                                                        onChange={(url) =>
                                                            formik.setFieldValue(
                                                                `items[${i}].backgroundImage`,
                                                                url
                                                            )
                                                        }
                                                        onDelete={() =>
                                                            formik.setFieldValue(
                                                                `items[${i}].backgroundImage`,
                                                                null
                                                            )
                                                        }
                                                        allowSkipCrop={true}
                                                        aspectRatio={305 / 180}
                                                        previewData={{
                                                            backgroundColor:
                                                                item.backgroundColor,
                                                            title:
                                                                item.title ??
                                                                undefined,
                                                            titleColor:
                                                                item.titleColor,
                                                            subtitle:
                                                                item.subTitle ??
                                                                undefined,
                                                            cta:
                                                                item.cta ??
                                                                undefined,
                                                        }}
                                                    />
                                                </LabeledField>

                                                <ErrorAwareLabel
                                                    text={`CTA (${localizedLanguage})`}
                                                    hasError={checkIfHasError(
                                                        `items[${i}].cta`
                                                    )}
                                                />
                                                <Input
                                                    name={`items[${i}].cta`}
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    value={item.cta ?? ''}
                                                />

                                                {formik.values.type ===
                                                    'external_url' && (
                                                    <>
                                                        <ErrorAwareLabel
                                                            text={`External url (${localizedLanguage})`}
                                                            hasError={checkIfHasError(
                                                                `items[${i}].externalUrl`
                                                            )}
                                                        />
                                                        <Input
                                                            name={`items[${i}].externalUrl`}
                                                            onBlur={
                                                                formik.handleBlur
                                                            }
                                                            onChange={
                                                                formik.handleChange
                                                            }
                                                            value={
                                                                item.externalUrl ??
                                                                ''
                                                            }
                                                        />
                                                    </>
                                                )}

                                                <ErrorAwareLabel
                                                    text={`Title (${localizedLanguage})`}
                                                    hasError={checkIfHasError(
                                                        `items[${i}].title`
                                                    )}
                                                />
                                                <TextField
                                                    name={`items[${i}].title`}
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    value={item.title ?? ''}
                                                    maxLength={MAX_LENGTH}
                                                    suggestedLength={
                                                        SUGGESTED_TITLE_LENGTH
                                                    }
                                                />

                                                <ErrorAwareLabel
                                                    text={`Subtitle (${localizedLanguage})`}
                                                    hasError={checkIfHasError(
                                                        `items[${i}].subTitle`
                                                    )}
                                                />
                                                <TextField
                                                    name={`items[${i}].subTitle`}
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    value={item.subTitle ?? ''}
                                                    maxLength={MAX_LENGTH}
                                                    suggestedLength={
                                                        SUGGESTED_SUBTITLE_LENGTH
                                                    }
                                                />

                                                <BackgroundColorPicker
                                                    value={item.backgroundColor}
                                                    onChange={(value) => {
                                                        formik.setFieldValue(
                                                            `items[${i}].backgroundColor`,
                                                            value
                                                        );
                                                        if (value !== null) {
                                                            const colorOption = BACKGROUND_COLORS.find(
                                                                (c) =>
                                                                    c.value ===
                                                                    value
                                                            );
                                                            formik.setFieldValue(
                                                                `items[${i}].titleColor`,
                                                                colorOption?.titleColor ??
                                                                    null
                                                            );
                                                            formik.setFieldValue(
                                                                `items[${i}].subtitleColor`,
                                                                colorOption?.subtitleColor ??
                                                                    null
                                                            );
                                                            formik.setFieldValue(
                                                                `items[${i}].buttonColor`,
                                                                colorOption?.buttonColor ??
                                                                    null
                                                            );
                                                        } else {
                                                            formik.setFieldValue(
                                                                `items[${i}].titleColor`,
                                                                null
                                                            );
                                                            formik.setFieldValue(
                                                                `items[${i}].subtitleColor`,
                                                                null
                                                            );
                                                            formik.setFieldValue(
                                                                `items[${i}].buttonColor`,
                                                                null
                                                            );
                                                        }
                                                    }}
                                                    language={item.language}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </Section>
                        </div>

                        <div className={style.right_side}>
                            <Section title="App Preview" noCard>
                                <div className={style.preview_container}>
                                    <PreviewProvider
                                        key={lang}
                                        initialRegion={ApplicationTargetEnum.QC}
                                        initialLanguage={lang}
                                    >
                                        <PreviewThemeProvider
                                            region={ApplicationTargetEnum.QC}
                                        >
                                            <AcvtCanvas
                                                changeLang={changeLanguage}
                                            >
                                                <Acvt
                                                    backgroundColor={
                                                        Colors.UNKNOWN_BACKGROUND_RED
                                                    }
                                                >
                                                    <AcvtHomePage
                                                        key={formik.values.id}
                                                        onEditMode={true}
                                                        hideMealDeals
                                                        featuredCarousel={
                                                            <MainBanner
                                                                image={
                                                                    formik
                                                                        .values
                                                                        .items[
                                                                        acvtItemsIndex as number
                                                                    ]
                                                                        .backgroundImage
                                                                }
                                                                backgroundColor={
                                                                    formik
                                                                        .values
                                                                        .items[
                                                                        acvtItemsIndex as number
                                                                    ]
                                                                        .backgroundColor
                                                                }
                                                                title={
                                                                    formik
                                                                        .values
                                                                        .items[
                                                                        acvtItemsIndex as number
                                                                    ].title ??
                                                                    undefined
                                                                }
                                                                titleColor={
                                                                    formik
                                                                        .values
                                                                        .items[
                                                                        acvtItemsIndex as number
                                                                    ].titleColor
                                                                }
                                                                subtitle={
                                                                    formik
                                                                        .values
                                                                        .items[
                                                                        acvtItemsIndex as number
                                                                    ]
                                                                        .subTitle ??
                                                                    undefined
                                                                }
                                                                subtitleColor={
                                                                    formik
                                                                        .values
                                                                        .items[
                                                                        acvtItemsIndex as number
                                                                    ]
                                                                        .subtitleColor
                                                                }
                                                                cta={
                                                                    formik
                                                                        .values
                                                                        .items[
                                                                        acvtItemsIndex as number
                                                                    ].cta ??
                                                                    undefined
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <PreviewBottomTabBar
                                                        selectedTab={
                                                            selectedTab
                                                        }
                                                        onTabChange={
                                                            setSelectedTab
                                                        }
                                                        readOnly
                                                    />
                                                </Acvt>
                                            </AcvtCanvas>
                                        </PreviewThemeProvider>
                                    </PreviewProvider>
                                </div>
                            </Section>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};
